import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Rectangle2 from '../media/LandingPageNew/Rectangle2.png';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import { Card, CardContent } from "@/components/shadcn/card";
import { Button } from "@/components/shadcn/button";
import kidsOnLaptop from '../media/LandingPageNew/miscPics/kidsOnLaptop.png';
import lines from '../media/LandingPageNew/lines.png';
import topLeftStars from '../media/LandingPageNew/stars/top-left-stars.svg';
import topRightStars from '../media/LandingPageNew/stars/top-right-stars.svg';
import topMiddleStars from '../media/LandingPageNew/stars/top-middle-stars.svg';
import middleLeftStars from '../media/LandingPageNew/stars/middle-left-stars.svg';
import middleRightStars from '../media/LandingPageNew/stars/middle-right-stars.svg';
import bottomLeftStars from '../media/LandingPageNew/stars/bottom-left-stars.svg';
import bottomRightStars from '../media/LandingPageNew/stars/bottom-right-stars.svg';
import book from '../media/LandingPageNew/heroIcons/book.svg';
import paperPlane from '../media/LandingPageNew/heroIcons/paperPlane.svg';
import pencil from '../media/LandingPageNew/heroIcons/pencil.svg';
import setSquare from '../media/LandingPageNew/heroIcons/setSquare.svg';
import trophy from '../media/LandingPageNew/heroIcons/trophy.svg';
import createAccount from '../media/LandingPageNew/buttons/createAccount.svg';
import tryFreeSession from '../media/LandingPageNew/buttons/TryFreeSession.svg';
import redCard from '../media/LandingPageNew/cards/RedCard.svg';
import purpleCard from '../media/LandingPageNew/cards/PurpleCard.svg';
import HowItWorksInteractive from './HowItWorksInteractive';
import GoogleReviewsReel, { GoogleG, Stars, useGoogleReviews } from './GoogleReviewsReel';
import SiteFooter from './SiteFooter';
import CallbackPopup from './CallbackPopup';
import useMediaQuery, { useIsPhone } from '../hooks/useMediaQuery';

const BELOW_LG_QUERY = '(max-width: 1023.98px)';
import backgroundRectangle from '../media/LandingPageNew/FAQs/backgroundRectangle.png';
import mobileBackgroundRectangle from '../media/LandingPageNew/FAQs/mobileBackgroundRectangle.png';
import classroom from '../media/LandingPageNew/FAQs/classroom.png';
import mobileClassroom from '../media/LandingPageNew/FAQs/MobileClassroom.png';

// One tile of the stats strip. The card art (a coloured grid-lined rounded rect)
// is a cover background so tiles share one height however many are in the row;
// without `art` the tile is a plain white card with a light border.
const StatCard = ({ art, badgeClass, textClass, label, heading, value, footer }) => (
  <div
    className={`relative h-48 lg:h-52 rounded-[20px] bg-cover bg-center flex flex-col items-center justify-center text-center px-6 pt-10 ${art ? '' : 'bg-white border-2 border-[#E5E0F5]'}`}
    style={art ? { backgroundImage: `url(${art})` } : undefined}
  >
    <div className={`absolute top-4 left-4 rounded-lg px-4 py-2 font-medium text-lg ${badgeClass}`}
         style={{ fontFamily: 'Fredoka, sans-serif' }}>
      {label}
    </div>
    <h3 className={`text-lg md:text-xl font-medium mb-2 ${textClass}`} style={{ fontFamily: 'Fredoka, sans-serif' }}>
      {heading}
    </h3>
    <div className={`text-5xl ${textClass}`} style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
      {value}
    </div>
    {footer && <div className="flex items-center gap-2 mt-2">{footer}</div>}
  </div>
);

const LandingPageNew = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section');
  const googleReviews = useGoogleReviews();
  const hasGoogleRating = !!googleReviews?.configured && googleReviews.rating > 0;
  const isPhone = useIsPhone();
  const isBelowLg = useMediaQuery(BELOW_LG_QUERY);

  // Nav/footer links on other public pages (e.g. /about) send us here as /?section=<id>.
  useEffect(() => {
    if (!section) return;
    const scrollToSection = (behavior) => document.getElementById(section)?.scrollIntoView({ behavior });
    scrollToSection('smooth');
    // Images and the reviews reel above the section are still loading in on a fresh
    // page load, which pushes the target down; re-align once the layout has settled.
    const realign = setTimeout(() => scrollToSection('auto'), 1200);
    return () => clearTimeout(realign);
  }, [section]);

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4">
        <div 
          className="relative w-full h-[690px] md:h-[604px] bg-cover bg-center bg-no-repeat rounded-2xl max-md:overflow-hidden"
          style={{ backgroundImage: `url(${Rectangle2})` }}
        >
          {/* Grid lines overlay */}
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat rounded-2xl"
            style={{ backgroundImage: `url(${lines})` }}
          />
          
          {/* Star overlays - positioned above grid lines */}
          <img 
            src={topLeftStars} 
            alt="" 
            className="absolute top-4 left-4 w-auto h-auto pointer-events-none z-20"
          />
          <img 
            src={topRightStars} 
            alt="" 
            className="absolute top-4 right-6 w-auto h-auto pointer-events-none z-20"
          />
          <img 
            src={topMiddleStars} 
            alt="" 
            className="absolute top-9 left-[32%] transform -translate-x-1/2 w-auto h-auto pointer-events-none z-20 hidden md:block"
          />

          <img 
            src={topMiddleStars} 
            alt="" 
            className="absolute top-8 left-[72%] transform -translate-x-1/2 w-auto h-auto pointer-events-none z-20 hidden md:block"
          />
          <img 
            src={middleLeftStars} 
            alt="" 
            className="absolute top-1/2 left-5 transform -translate-y-1/2 w-auto h-auto pointer-events-none z-20 hidden md:block"
          />
          <img 
            src={middleRightStars} 
            alt="" 
            className="absolute top-1/2 right-4 transform -translate-y-1/2 w-auto h-auto pointer-events-none z-20"
          />
          <img 
            src={bottomLeftStars} 
            alt="" 
            className="absolute bottom-4 left-4 w-auto h-auto pointer-events-none z-20"
          />
          <img 
            src={bottomRightStars} 
            alt="" 
            className="absolute bottom-4 right-4 w-auto h-auto pointer-events-none z-20"
          />
          
          {/* Hero Icons - positioned as percentages to scale with Rectangle2 */}
          <img 
            src={book} 
            alt="" 
            className="absolute w-auto h-auto pointer-events-none z-25"
            style={{ 
                left: isPhone ? '7%': '6.96%',
                top: isPhone ? '81.5%' : '79.47%'
            }}
          />
          <img 
            src={paperPlane}
            alt=""
            className="absolute w-auto h-auto pointer-events-none z-25 hidden md:block"
            style={{ 
                left: '79.25%',
                top: '8.56%'
            }}
          />
          <img 
            src={pencil} 
            alt="" 
            className="absolute w-auto h-auto pointer-events-none z-25 hidden lg:block"
            style={{ left: '29.41%', top: '70.53%' }}
          />
          <img 
            src={setSquare}
            alt=""
            className="absolute w-auto h-auto pointer-events-none z-25 hidden md:block"
            style={{ left: '0%', top: '2.61%' }}
          />
          <img 
            src={trophy}
            alt=""
            className="absolute w-auto h-auto pointer-events-none z-25 hidden md:block"
            style={{ 
              left: '74.01%', 
              top: '76.66%'
            }}
          />
          
          {/* Hero content container - ready for your content */}
          <div className="relative max-w-[1360px] mx-auto h-full px-4 md:px-6 lg:px-8 flex items-center justify-center z-30" style={{ transform: 'translateY(-30px)' }}>
            <div className="text-center">
              <h1 className="text-white text-5xl lg:text-6xl xl:text-7xl leading-tight max-md:text-4xl mb-2" 
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
                Maths is Now <span style={{ color: '#B2FA18', textDecoration: 'underline', textDecorationStyle: 'wavy', textDecorationColor: 'white', textDecorationThickness: '4px', textUnderlineOffset: '8px', fontWeight: '600' }}>Super Fun!</span>
              </h1>
              <h2 className="text-white text-5xl lg:text-6xl xl:text-7xl leading-tight max-md:text-4xl" 
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '400' }}>
                Where Maths Turns Into <span style={{ color: '#C4E57D', textDecoration: 'underline', textDecorationStyle: 'wavy', textDecorationColor: 'white', textDecorationThickness: '4px', textUnderlineOffset: '8px', fontWeight: '600' }}>Playtime!</span>
              </h2>
              <p className="hidden md:block text-white text-lg lg:text-xl mt-6 max-w-4xl mx-auto leading-relaxed" 
                 style={{ fontFamily: 'Poppins, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Interactive maths platform for Years 2-6 with AI tutor Addy and real teachers. No more boring worksheets - just fun learning adventures!
              </p>
              
              {/* Action Buttons */}
              <div className="relative flex justify-center mt-8">
                <div className="relative">
                  <Button
                    onClick={() => navigate('/register')}
                    className="bg-[#C4E57D] border-2 border-[#9FD42A] text-black hover:bg-[#9FD42A] hover:border-[#9FD42A] rounded-3xl px-6 py-3 text-lg font-semibold max-md:h-12"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}
                  >
                    Book free assessment now
                  </Button>
                  <img 
                    src={tryFreeSession} 
                    alt="Try Free Session"
                    onClick={() => navigate('/register')}
                    className="absolute top-12 left-1/2 transform -translate-x-1/2 lg:left-48 md:transform-none cursor-pointer hover:scale-105 transition-transform duration-200"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-8">
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 ${hasGoogleRating ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          <StatCard art={purpleCard} badgeClass="bg-[#765AD8] text-white" textClass="text-white"
                    label="Classes" heading="Live Lessons delivered" value="500+" />
          <StatCard art={redCard} badgeClass="bg-[#FD8C7C] text-white" textClass="text-white"
                    label="Community" heading="Families supported" value="300+" />
          {hasGoogleRating && (
            <StatCard badgeClass="bg-[#F0EDFA] text-[#2F1E6B]" textClass="text-black"
                      label={<span className="flex items-center gap-2"><GoogleG className="w-5 h-5" />Google Reviews</span>}
                      heading="Rated by parents"
                      value={googleReviews.rating.toFixed(1)}
                      footer={
                        <>
                          <Stars rating={googleReviews.rating} />
                          {googleReviews.totalReviews ? (
                            <span className="text-sm text-gray-600" style={{ fontFamily: 'Poppins, sans-serif' }}>
                              from {googleReviews.totalReviews} reviews
                            </span>
                          ) : null}
                        </>
                      } />
          )}
        </div>
      </section>

      {/* How Nova Works Section */}
      <section id="how-it-works" className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-12">
        <div className="text-center">
          <h2 className="text-4xl lg:text-5xl xl:text-6xl leading-tight mb-6" 
              style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
            <span className="text-black">How Nova </span>
            <span 
              className="text-[#2F1E6B]" 
              style={{ 
                textDecoration: 'underline', 
                textDecorationStyle: 'wavy',
                textDecorationColor: '#B2FA18',
                textDecorationThickness: '4px', 
                textUnderlineOffset: '4px',
                fontWeight: '600'
              }}
            >
              Works
            </span>
          </h2>
          <p className="text-black text-base lg:text-lg max-w-4xl mx-auto leading-relaxed" 
             style={{ fontFamily: 'Poppins, sans-serif' }}>
            The perfect mix of smart AI technology and caring human teachers that makes learning maths super fun and effective!
          </p>
        </div>
      </section>

      {/* How It Works Interactive Section */}
      <section className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 pb-12">
        <HowItWorksInteractive />
      </section>

      {/* Background Rectangle Section - FAQs */}
      <section id="faqs" className="relative w-full py-12">
        <div className="relative w-full">
          {/* Background Rectangle */}
          <div 
            className="relative w-full h-auto bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: isBelowLg 
                ? `url(${mobileBackgroundRectangle})` 
                : `url(${backgroundRectangle})` 
            }}
          >
            <img 
              src={isBelowLg ? mobileBackgroundRectangle : backgroundRectangle} 
              alt="Background" 
              className="w-full h-auto object-cover max-h-screen max-md:hidden"
            />

            {/* Classroom Image Overlay. Below md the sizing <img> is hidden and this
                becomes a normal-flow, content-driven panel over the same background. */}
            <div className="absolute inset-0 flex flex-col landscape:flex-row lg:flex-row items-center px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 max-md:relative max-md:inset-auto max-md:py-6">
              {/* Classroom positioned left on desktop and mobile landscape, top on mobile portrait */}
              <div className="w-full landscape:w-1/2 lg:w-1/2 flex justify-center lg:justify-start items-center py-4 lg:p-8">
                <img 
                  src={isBelowLg ? mobileClassroom : classroom} 
                  alt="Classroom" 
                  className="w-full max-h-[40vh] landscape:max-h-[60vh] lg:max-h-none lg:max-w-full h-auto object-contain"
                />
              </div>
              
              {/* Accordion area - right on desktop and mobile landscape, bottom on mobile portrait */}
              <div className="w-full landscape:w-1/2 lg:w-1/2 p-4 lg:p-8">
                <div className="bg-transparent rounded-xl p-6">
                  <h3 className="text-xl lg:text-4xl font-semibold mb-0 text-center text-white" 
                      style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    Why Nova is Right for Your Kids?
                  </h3>
                  
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="year-groups" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        What year groups do you provide tuition for?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        We provide Maths lessons for children from years 2 to year 6
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="national-curriculum" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Do you follow the national curriculum?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Yes, our lesson content strictly follows the UK Maths National Curriculum, so will help your child to excel and progress in their school work
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="help-support" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Can my child ask for help when they're stuck?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Yes! Our two-layered support system means that children will receive help and support from both our digital assistant Addy, and from one of our expert instructors that oversee your child's learning sessions
                      </AccordionContent>
                    </AccordionItem>
                    
                    <AccordionItem value="progress-updates" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Will I be kept updated on my child's learning?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Yes! Our progress reports will keep you updated on your child's learning progress as they progress through our classroom learning sessions
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="sit-with-child" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Do I need to sit with my child while they learn?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        No. Nova is designed so children can learn independently. Our live lessons are overseen by expert tutors, while the platform provides clear explanations, guided practice and instant feedback. Parents are always welcome to support their child, but there's no need to sit with them during lessons or practice.
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="cancel-anytime" className="border-b border-[#6F51D6]">
                      <AccordionTrigger className="text-left text-white hover:text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Can I cancel anytime?
                      </AccordionTrigger>
                      <AccordionContent className="text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Yes. If you decide to cancel, your subscription will remain active until the end of your current billing period, so your child can continue using all of their remaining lessons and platform access until then.
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Parents Are Saying Section */}
      <section id="reviews" className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-12">
        <div className="flex flex-col md:flex-row">
          {/* Left side - Header */}
          <div className="w-full md:w-1/2">
            <h2 className="text-4xl lg:text-5xl xl:text-6xl leading-tight" 
                style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
              <span className="text-black">What </span>
              <span className="text-[#2F1E6B]">Mums</span>
              <span className="text-black"> and </span>
              <span className="text-[#2F1E6B]">Dads</span>
              <span className="text-black"> are Saying</span>
            </h2>
          </div>
          
          {/* Right side - Content */}
          <div className="w-full md:w-1/2 flex items-center md:pl-8">
            <p className="text-black text-base lg:text-lg leading-relaxed mt-6 md:mt-0" 
               style={{ fontFamily: 'Poppins, sans-serif' }}>
              Real results parents can see! Our unique blend of AI technology and expert tutors helps children build confidence, improve grades, and actually enjoy learning maths.
            </p>
          </div>
        </div>
        
        {/* Google reviews reel (falls back to built-in testimonials until the
            API cache is configured — see GoogleReviewsReel) */}
        <GoogleReviewsReel data={googleReviews} />
      </section>

      {/* Choose Your Perfect Plan Section */}
      <section id="pricing" className="relative w-full px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 py-12">
        <div className="text-center">
          <h2 className="text-4xl lg:text-5xl xl:text-6xl leading-tight" 
              style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
            <span className="text-black">Choose your </span>
            <span 
              className="text-[#2F1E6B]" 
              style={{ 
                textDecoration: 'underline', 
                textDecorationStyle: 'wavy',
                textDecorationColor: '#B2FA18',
                textDecorationThickness: '4px', 
                textUnderlineOffset: '6px',
                fontWeight: '600'
              }}
            >
              Perfect Plan
            </span>
          </h2>
          <p className="text-black text-base lg:text-lg max-w-4xl mx-auto leading-relaxed mt-6" 
             style={{ fontFamily: 'Poppins, sans-serif' }}>
            The perfect mix of smart AI technology and caring human teachers that makes learning maths super fun and effective!
          </p>
        </div>
        
        {/* 1-1 Tuition Plan — premium tier above the group plans (Stripe price: STRIPE_ONE_TO_ONE_PRICE_ID) */}
        <div className="mt-12 max-w-md mx-auto">
          <Card className="relative overflow-hidden bg-gradient-to-br from-white via-white to-[#F0EDFA] border-2 border-[#2F1E6B] shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            {/* Premium tag, top corner */}
            <div className="absolute top-0 right-0 bg-gradient-to-r from-[#2F1E6B] to-[#5C3BD1] text-white text-xs tracking-widest px-5 py-2 rounded-bl-2xl"
                 style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}>
              ★ PREMIUM
            </div>
            <CardContent className="p-6">
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold text-black mb-2"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  1-1 Tuition Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4"
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £150/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    8 live tutoring sessions per month — 1-1 personalised learning with a tutor focused entirely on your child
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Personalised, interactive maths lessons tailored to your child's level
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Support from expert learning instructors who guide and oversee your child's progress
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Core Skills practice to strengthen key foundations and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Personalised model answers that highlight where your child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Detailed progress reports showing your child's strengths, gaps and next steps
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register?plan=one_to_one')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#2F1E6B] border-2 border-[#241553] text-white hover:bg-[#241553] hover:border-[#241553] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Single Plan Card */}
          <Card className="bg-white border-2 border-[#2F1E6B] shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            <CardContent className="p-6">
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold text-black mb-2" 
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Single Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4" 
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £80/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Access for 1 child
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    8 live, tutor-led classroom sessions per month
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Personalised, interactive maths lessons tailored to your child's level
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Support from expert learning instructors who guide and oversee your child's progress
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Core Skills practice to strengthen key foundations and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Personalised model answers that highlight where your child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#2F1E6B] mr-2">✱</span>
                    Detailed progress reports showing your child's strengths, gaps and next steps
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#5C3BD1] border-2 border-[#4b2cba] text-white hover:bg-[#4b2cba] hover:border-[#4b2cba] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Sibling Plan Card */}
          <Card className="bg-white border-2 border-[#FD7764] shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            <CardContent className="p-6">
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold text-black mb-2" 
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Sibling Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4" 
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £120/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Access for 2 children
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    8 live, tutor-led classroom sessions per child, per month
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Personalised, interactive maths lessons tailored to each child's level
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Support from expert learning instructors who guide and oversee each child's progress
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Core Skills practice to strengthen key foundations and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Personalised model answers that highlight where your child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Detailed progress reports showing each child's strengths, gaps and next steps
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#FD7764] border-2 border-[#FC4E36] text-white hover:bg-[#FC4E36] hover:border-[#FC4E36] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Family Plan Card */}
          <Card className="bg-white border-2 border-[#BAE066] shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            <CardContent className="p-6">
              <div className="flex flex-col">
                <h3 className="text-2xl font-bold text-black mb-2" 
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Family Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4" 
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £150/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Access for 3 children
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    8 live, tutor-led classroom sessions per child, per month
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Personalised, interactive maths lessons tailored to each child's level
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Support from expert learning instructors who guide and oversee each child's progress
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Core Skills practice to strengthen key foundations and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Personalised model answers that highlight where your child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#BAE066] mr-2">✱</span>
                    Detailed progress reports showing each child's strengths, gaps and next steps
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/register')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#BAE066] border-2 border-[#A7D73C] text-white hover:bg-[#A7D73C] hover:border-[#A7D73C] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Platform-Only Plans — visually distinct tier beneath the classroom plans */}
        <div className="text-center mt-16">
          <h3 className="text-2xl lg:text-3xl text-black"
              style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '500' }}>
            Just need the platform? <span className="text-[#2F1E6B]" style={{ fontWeight: '600' }}>Platform-Only Plans</span>
          </h3>
          <p className="text-black text-base lg:text-lg max-w-3xl mx-auto leading-relaxed mt-3"
             style={{ fontFamily: 'Poppins, sans-serif' }}>
            For independent learners — full access to our self-paced maths platform, without the live, tutor-led classroom sessions.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8 max-w-4xl mx-auto">
          {/* Platform Single Plan — Stripe price: STRIPE_PLATFORM_ONLY_PRICE_ID */}
          <Card className="bg-white border-2 border-dashed border-[#5C3BD1] shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            <CardContent className="p-6">
              <div className="flex flex-col h-full">
                <h3 className="text-2xl font-bold text-black mb-2"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Platform Single Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4"
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £20/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Access for 1 child
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Core Skills practice to build fluency and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Personalised model answers that highlight where your child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Progress radar to help parents see strengths, gaps and areas to revisit
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#5C3BD1] mr-2">✱</span>
                    Self-paced learning with interactive content, quizzes and practice activities
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register?plan=platform_only')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#5C3BD1] border-2 border-[#4b2cba] text-white hover:bg-[#4b2cba] hover:border-[#4b2cba] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Platform Family Plan — Stripe price: STRIPE_PLATFORM_ONLY_FAMILY_PRICE_ID */}
          <Card className="bg-white border-2 border-dashed border-[#FD7764] shadow-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1.5">
            <CardContent className="p-6">
              <div className="flex flex-col h-full">
                <h3 className="text-2xl font-bold text-black mb-2"
                    style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Platform Family Plan
                </h3>
                <p className="text-xl font-semibold text-black mb-4"
                   style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  £30/month
                </p>
                <ul className="space-y-3 text-black" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Access for 2 children
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Full access to lesson content at home, covering the primary maths curriculum
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Core Skills practice to build fluency and close learning gaps
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Worksheet generator for extra targeted practice at home
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Personalised model answers that highlight where each child went wrong and guide them through the correct method step by step
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Progress radar to help parents see strengths, gaps and areas to revisit
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#FD7764] mr-2">✱</span>
                    Self-paced learning with interactive content, quizzes and practice activities
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/register?plan=platform_only_family')}
                  className="max-md:h-12 mt-6 w-5/6 mx-auto bg-[#FD7764] border-2 border-[#FC4E36] text-white hover:bg-[#FC4E36] hover:border-[#FC4E36] rounded-3xl"
                  style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                >
                  Start free trial
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="flex-1">
        {/* Final Call-to-Action Section */}
        <div className="px-8 sm:px-12 md:px-6 lg:px-8 max-md:px-4 mt-16 mb-16">
          <div className="flex flex-col md:flex-row gap-8 md:gap-6">
            {/* Left Side - CTA Card */}
            <div className="flex-[2]">
              <Card className="bg-gradient-to-br from-[#6047D2] to-[#4A2E9B] border-none shadow-xl h-full">
                <CardContent className="p-8 md:p-12 flex flex-col justify-center h-full">
                  <div className="text-center">
                    <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold text-white leading-tight mb-8"
                        style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Start a <span style={{ color: '#B2FA18' }}>FREE trial</span> Now!<br />
                      Maths is Now <span style={{ color: '#C4E57D' }}>Super Fun!</span>
                    </h2>
                    <div className="flex justify-center">
                      <Button
                        onClick={() => navigate('/register')}
                        className="mt-4 px-8 py-3 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#2F1E6B] rounded-xl transition-all duration-300 max-md:h-12"
                        style={{ fontFamily: 'Fredoka, sans-serif', fontWeight: '600' }}
                      >
                        Book Free Assessment
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Kids on Laptop Image */}
            <div className="flex-1">
              <div className="flex justify-center md:justify-end">
                <img 
                  src={kidsOnLaptop} 
                  alt="Kids learning on laptop" 
                  className="w-full h-auto max-w-lg object-contain"
                />
              </div>
            </div>
          </div>
        </div>

        {/* <div id="contact-us" className="h-96 bg-white flex items-center justify-center">
          <h2 className="text-2xl font-bold">Contact us Section</h2>
        </div> */}
      </main>

      <SiteFooter />
      <CallbackPopup />
    </div>
  );
};

export default LandingPageNew;
